/**
 * Socle commun des graphiques de statistiques (design system, GraphiquePrevisions,
 * GraphiqueActivite, GraphiqueRetention) : panneau, entête avec légende et bouton « Tableau
 * des données », tracé SVG à l'échelle de la largeur mesurée, infobulle, parcours au clavier
 * (une tabulation, ← → Début Fin) et tableau des données pour les lecteurs d'écran.
 */
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { Icone } from '../composants/Icone';
import { ESPACE_FINE, ESPACE_INSECABLE as E, nombre } from '../outils';

export type ClasseGraphique =
  'fc-graphique-previsions' | 'fc-graphique-activite' | 'fc-graphique-retention';

/** Marges du tracé : place de la valeur écrite en haut, de l'axe des dates en bas. */
export const HAUT = 16;
export const BAS = 24;
export const HAUTEUR = 200;
export const HAUTEUR_VIDE = 112;

const FORMAT_JOUR_MOIS = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' });
const FORMAT_COURT = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' });
const FORMAT_LONG = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});
const FORMAT_TABLEAU = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});

/** Date locale d'une journée du paquet au format AAAA-MM-JJ. */
export function dateDuJour(cle: string): Date {
  const [a, m, j] = cle.split('-').map(Number);
  return new Date(a!, m! - 1, j!);
}

const insecable = (texte: string) => texte.replace(/ /g, E);
export const majuscule = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
/** « 8 septembre ». */
export const jourMois = (cle: string) => insecable(FORMAT_JOUR_MOIS.format(dateDuJour(cle)));
/** « 14 oct. ». */
export const jourCourt = (cle: string) => insecable(FORMAT_COURT.format(dateDuJour(cle)));
/** « samedi 26 septembre ». */
export const jourLong = (cle: string) => FORMAT_LONG.format(dateDuJour(cle));
/** « Sam. 26 sept. ». */
export const jourTableau = (cle: string) => majuscule(FORMAT_TABLEAU.format(dateDuJour(cle)));
/** « 92 % ». */
export const pourcent = (v: number) => `${Math.round(v)}${ESPACE_FINE}%`;

/** Graduations rondes, cinq intervalles au plus. */
export function graduer(max: number): { pas: number; haut: number } {
  const pas =
    [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10_000].find(
      (p) => Math.ceil(max / p) <= 5,
    ) ?? 10 ** Math.ceil(Math.log10(max / 5));
  return { pas, haut: Math.max(pas, Math.ceil(max / pas) * pas) };
}

let contexte: CanvasRenderingContext2D | null | undefined;
/** Largeur d'une graduation en 12 px, pour réserver la marge de gauche. */
export function largeurTexte(texte: string): number {
  if (contexte === undefined) contexte = document.createElement('canvas').getContext('2d');
  if (!contexte) return texte.length * 7;
  contexte.font = `12px ${getComputedStyle(document.body).fontFamily}`;
  return contexte.measureText(texte).width;
}

/** Barre arrondie de 4 px en haut seulement, carrée sur la ligne de base. */
export function cheminBarre(x: number, yBas: number, yHaut: number, l: number, arrondi: boolean) {
  const r = arrondi ? Math.min(4, yBas - yHaut, l / 2) : 0;
  return (
    `M${x} ${yBas}V${yHaut + r}` +
    (r
      ? `A${r} ${r} 0 0 1 ${x + r} ${yHaut}H${x + l - r}A${r} ${r} 0 0 1 ${x + l} ${yHaut + r}`
      : `H${x + l}`) +
    `V${yBas}Z`
  );
}

/** Largeur du conteneur, suivie quand elle change. */
export function useLargeur(ref: RefObject<HTMLElement | null>): number {
  const [largeur, setLargeur] = useState(0);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    setLargeur(element.clientWidth);
    const observateur = new ResizeObserver(() => setLargeur(element.clientWidth));
    observateur.observe(element);
    return () => observateur.disconnect();
  }, [ref]);
  return largeur;
}

const indexDe = (cible: EventTarget | null): number | null => {
  const valeur = (cible as Element | null)?.getAttribute?.('data-index');
  return valeur == null ? null : Number(valeur);
};

/**
 * Colonne survolée ou focalisée, et focus itinérant : une seule colonne a `tabindex="0"`
 * (au départ `depart`, aujourd'hui ou la semaine en cours).
 */
export function useColonnes(n: number, depart: number) {
  const [actif, setActif] = useState(-1);
  const [courant, setCourant] = useState(depart);
  const groupe = useRef<SVGGElement>(null);
  useEffect(() => {
    setCourant(depart);
    setActif(-1);
  }, [n, depart]);

  return {
    actif,
    courant,
    groupe,
    trace: {
      onFocus(e: FocusEvent<HTMLDivElement>) {
        const i = indexDe(e.target);
        if (i === null) return;
        setActif(i);
        setCourant(i);
      },
      onBlur(e: FocusEvent<HTMLDivElement>) {
        if (!groupe.current?.contains(e.relatedTarget as Node | null)) setActif(-1);
      },
      onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
        const i = indexDe(e.target);
        if (i === null) return;
        const j = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: n - 1 }[e.key];
        if (j === undefined) return;
        e.preventDefault();
        const k = Math.max(0, Math.min(n - 1, j));
        setCourant(k);
        groupe.current?.querySelector<SVGGElement>(`[data-index="${k}"]`)?.focus();
      },
    },
    /** Survol du tracé : la zone d'une colonne couvre tout son pas. */
    survoler(e: PointerEvent<SVGSVGElement>, gauche: number, pas: number) {
      const x = e.clientX - e.currentTarget.getBoundingClientRect().left;
      const i = x < gauche ? -1 : Math.min(n - 1, Math.floor((x - gauche) / pas));
      if (i !== actif) setActif(i);
    },
    quitter() {
      if (!groupe.current?.contains(document.activeElement)) setActif(-1);
    },
  };
}

/** Place l'infobulle à droite de la colonne active, à gauche si elle déborderait. */
export function useInfobulle(
  bulle: RefObject<HTMLDivElement | null>,
  actif: number,
  centre: number,
  ecart: number,
  largeur: number,
) {
  useLayoutEffect(() => {
    const element = bulle.current;
    if (!element || actif < 0) return;
    const l = element.offsetWidth;
    let x = centre + ecart;
    if (x + l > largeur) x = centre - ecart - l;
    element.style.transform = `translate(${Math.round(x)}px, ${HAUT - 8}px)`;
  }, [bulle, actif, centre, ecart, largeur]);
}

export interface LigneDuTableau {
  cle: string;
  cellules: ReactNode[];
  courante?: boolean;
  /** Indices des cellules sans valeur (texte secondaire). */
  sansValeur?: number[];
}

export interface CadreDeGraphiqueProps {
  classe: ClasseGraphique;
  titre: string;
  resume: string;
  /** Clés de la légende (`li`), absentes à l'état vide. */
  legende: ReactNode;
  /** Message de l'état vide, ou `null` quand il y a des données. */
  vide: { titre: string; texte: string } | null;
  chargement: boolean;
  tableau: { legende: string; entetes: string[]; lignes: LigneDuTableau[] };
  /** Gestionnaires du parcours au clavier (`useColonnes`), posés sur le conteneur HTML : sur
   * un élément SVG, ils le rendraient focalisable. */
  trace: ReturnType<typeof useColonnes>['trace'];
  /** Tracé : reçoit la largeur mesurée (0 avant la première mesure). */
  children: (largeur: number) => ReactNode;
}

export function CadreDeGraphique({
  classe: P,
  titre,
  resume,
  legende,
  vide,
  chargement,
  tableau,
  trace,
  children,
}: CadreDeGraphiqueProps) {
  const id = useId();
  const zone = useRef<HTMLDivElement>(null);
  const largeur = useLargeur(zone);
  const [montrerTableau, setMontrerTableau] = useState(false);
  const [hauteurTrace, setHauteurTrace] = useState<number | null>(null);

  return (
    <figure className={P} aria-labelledby={`${id}-titre`} aria-busy={chargement || undefined}>
      <div className={`${P}__entete`}>
        <div className={`${P}__titres`}>
          <h2 className={`${P}__titre`} id={`${id}-titre`}>
            {titre}
          </h2>
          <p className={`${P}__resume`}>{resume}</p>
        </div>
        {!vide && (
          <div className={`${P}__outils`}>
            <ul className={`${P}__legende`} aria-label="Légende">
              {legende}
            </ul>
            <button
              type="button"
              className="fc-bouton fc-bouton--discret fc-bouton--compact fc-bouton--icone"
              aria-pressed={montrerTableau}
              aria-controls={`${id}-donnees`}
              aria-label="Tableau des données"
              title="Tableau des données"
              onClick={() => {
                if (!montrerTableau) setHauteurTrace(zone.current?.offsetHeight ?? null);
                setMontrerTableau(!montrerTableau);
              }}
            >
              <Icone nom="table" />
            </button>
          </div>
        )}
      </div>
      <div
        className={`${P}__trace`}
        ref={zone}
        hidden={!vide && montrerTableau}
        {...(vide ? {} : trace)}
      >
        {vide ? (
          <>
            <svg
              className={`${P}__svg`}
              width={largeur}
              height={HAUT + HAUTEUR_VIDE + BAS}
              aria-hidden="true"
            >
              <line
                className={`${P}__grille`}
                x1={0}
                x2={largeur}
                y1={HAUT + HAUTEUR_VIDE + 0.5}
                y2={HAUT + HAUTEUR_VIDE + 0.5}
              />
            </svg>
            <div className={`${P}__vide`}>
              <p className={`${P}__vide-titre`}>{vide.titre}</p>
              <p className={`${P}__vide-texte`}>{vide.texte}</p>
            </div>
          </>
        ) : (
          children(largeur)
        )}
      </div>
      {!vide && (
        <div
          className={montrerTableau ? `${P}__donnees` : `${P}__donnees fc-sr`}
          id={`${id}-donnees`}
          style={montrerTableau && hauteurTrace ? { height: hauteurTrace } : undefined}
        >
          <table className={`${P}__tableau`}>
            <caption>{tableau.legende}</caption>
            <thead>
              <tr>
                {tableau.entetes.map((entete) => (
                  <th key={entete} scope="col">
                    {entete}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableau.lignes.map((ligne) => (
                <tr key={ligne.cle} aria-current={ligne.courante ? 'date' : undefined}>
                  {ligne.cellules.map((cellule, i) => (
                    <td key={i} data-sans-valeur={ligne.sansValeur?.includes(i) ? '' : undefined}>
                      {cellule}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </figure>
  );
}

/** Nombre formaté, réexporté pour les graphiques. */
export { nombre };
